import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import type {
  GeneratedLetterView, GenericRequestDecisionContext, GenericRequestView, LetterTemplateView,
  RequestFormField, RequestStatus, RequestTypeView,
} from '../types'

const TYPES = '/v1/request-types'
const REQUESTS = '/v1/requests'
const LETTERS = '/v1/letters'

export function useRequestTypes(activeOnly = false) {
  return useQuery({
    queryKey: ['requests', 'types', activeOnly],
    queryFn: async () =>
      (await apiClient.get<RequestTypeView[]>(TYPES, { params: { activeOnly } })).data,
  })
}

/** The form a type asks for — custom field definitions, in the order they were arranged. */
export function useRequestForm(typeId: string | undefined) {
  return useQuery({
    queryKey: ['requests', 'types', typeId, 'form'],
    enabled: typeId != null && typeId !== '',
    queryFn: async () => (await apiClient.get<RequestFormField[]>(`${TYPES}/${typeId}/form`)).data,
  })
}

export function useRequestTypeMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['requests'] })

  return {
    create: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<RequestTypeView>(TYPES, body)).data,
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        (await apiClient.put<RequestTypeView>(`${TYPES}/${id}`, body)).data,
      onSuccess: invalidate,
    }),
    deactivate: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<RequestTypeView>(`${TYPES}/${id}/deactivate`)).data,
      onSuccess: invalidate,
    }),
  }
}

export function useMyRequests() {
  return useQuery({
    queryKey: ['requests', 'mine'],
    queryFn: async () => (await apiClient.get<GenericRequestView[]>(`${REQUESTS}/my`)).data,
  })
}

export function useRequests(requestTypeId?: string, status?: RequestStatus) {
  return useQuery({
    queryKey: ['requests', 'list', requestTypeId, status],
    queryFn: async () =>
      (await apiClient.get<GenericRequestView[]>(REQUESTS, {
        params: { requestTypeId: requestTypeId || undefined, status: status || undefined },
      })).data,
  })
}

/** The request, its form paired with the answers, and its attachments — one call. */
export function useRequestContext(id: string | null) {
  return useQuery({
    queryKey: ['requests', id, 'context'],
    enabled: id != null,
    queryFn: async () =>
      (await apiClient.get<GenericRequestDecisionContext>(`${REQUESTS}/${id}/context`)).data,
  })
}

export function useRequestMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['requests'] })
    void queryClient.invalidateQueries({ queryKey: ['approvals'] })
  }

  return {
    raise: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<GenericRequestView>(REQUESTS, body)).data,
      onSuccess: invalidate,
    }),
    update: useMutation({
      mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        (await apiClient.put<GenericRequestView>(`${REQUESTS}/${id}`, body)).data,
      onSuccess: invalidate,
    }),
    submit: useMutation({
      mutationFn: async (id: string) =>
        (await apiClient.post<GenericRequestView>(`${REQUESTS}/${id}/submit`)).data,
      onSuccess: invalidate,
    }),
    cancel: useMutation({
      mutationFn: async ({ id, reason }: { id: string; reason?: string }) =>
        (await apiClient.post<GenericRequestView>(`${REQUESTS}/${id}/cancel`, { reason })).data,
      onSuccess: invalidate,
    }),
  }
}

// --- letters ---

export function useLetterTemplates() {
  return useQuery({
    queryKey: ['letters', 'templates'],
    queryFn: async () => (await apiClient.get<LetterTemplateView[]>(`${LETTERS}/templates`)).data,
  })
}

export function useIssuedLetters(employeeId: string | undefined) {
  return useQuery({
    queryKey: ['letters', 'issued', employeeId],
    enabled: employeeId != null,
    queryFn: async () =>
      (await apiClient.get<GeneratedLetterView[]>(`${LETTERS}/issued`, { params: { employeeId } })).data,
  })
}

/**
 * Renders a template without issuing anything.
 *
 * A mutation rather than a query even though it is a GET: previewing is something the author
 * asks for, and a query would re-render on every window focus while they are mid-edit.
 */
export function useLetterPreview() {
  return useMutation({
    mutationFn: async ({ id, employeeId }: { id: string; employeeId: string }) =>
      (await apiClient.get<string>(`${LETTERS}/templates/${id}/preview`, {
        params: { employeeId },
        // The endpoint returns HTML, so the client must not try to parse it as JSON.
        headers: { Accept: 'text/html' },
        responseType: 'text',
      })).data,
  })
}

export function useLetterMutations() {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['letters'] })

  return {
    createTemplate: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<LetterTemplateView>(`${LETTERS}/templates`, body)).data,
      onSuccess: invalidate,
    }),
    updateTemplate: useMutation({
      mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
        (await apiClient.put<LetterTemplateView>(`${LETTERS}/templates/${id}`, body)).data,
      onSuccess: invalidate,
    }),
    issue: useMutation({
      mutationFn: async (body: Record<string, unknown>) =>
        (await apiClient.post<GeneratedLetterView>(LETTERS, body)).data,
      onSuccess: invalidate,
    }),
    revoke: useMutation({
      mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
        (await apiClient.post<GeneratedLetterView>(`${LETTERS}/issued/${id}/revoke`, { reason })).data,
      onSuccess: invalidate,
    }),
  }
}
